import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import type { TodoWithUsers } from '@stromo/shared';
import {
  buildTodoUpdate,
  todoToDraft,
  useCreateTodo,
  useDeleteTodo,
  useProjectTodos,
  useToggleTodoComplete,
  useUpdateTodo,
  type TodoDraft,
  type UpdateTodoVars,
} from '../lib/todos';
import { useSections } from '../lib/sections';
import { useAuthStore } from '../lib/authStore';
import { apiErrorMessage } from '../lib/api';
import { TodoMoveFields } from './TodoMoveFields';
import { TodoSectionTag } from './TodoSectionTag';

interface TodosPanelProps {
  projectId: number;
}

/** 'all' = alle Sektionen, 'none' = nur "Allgemein", Zahl = eine Sektion. */
type SectionFilter = 'all' | 'none' | number;

const matchesSection = (t: TodoWithUsers, filter: SectionFilter): boolean =>
  filter === 'all' || (filter === 'none' ? t.section_id === null : t.section_id === filter);

export function TodosPanel({ projectId }: TodosPanelProps) {
  const { user } = useAuthStore();
  const todosQ = useProjectTodos(projectId);
  const sectionsQ = useSections(projectId);
  const createMut = useCreateTodo(projectId);
  const updateMut = useUpdateTodo();
  const toggleMut = useToggleTodoComplete(projectId);
  const deleteMut = useDeleteTodo();

  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newSectionId, setNewSectionId] = useState<number | null>(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [sectionFilter, setSectionFilter] = useState<SectionFilter>('all');

  const isPrivileged = user?.role === 'admin' || user?.role === 'project_manager';
  const sections = sectionsQ.data ?? [];

  // Chip wählen = auch die Sektion fürs nächste neue Todo vorbelegen.
  const selectFilter = (f: SectionFilter) => {
    setSectionFilter(f);
    if (f !== 'all') setNewSectionId(f === 'none' ? null : f);
  };

  const handleCreate = () => {
    const t = newTitle.trim();
    if (!t) return;
    createMut.mutate(
      { title: t, description: newDescription.trim() || null, section_id: newSectionId },
      {
        onSuccess: () => {
          setNewTitle('');
          setNewDescription('');
        },
      },
    );
  };

  const { open, completed, openCountBySection } = useMemo(() => {
    const list = todosQ.data ?? [];
    const counts = new Map<SectionFilter, number>();
    for (const t of list) {
      if (t.completed_at !== null) continue;
      const key: SectionFilter = t.section_id ?? 'none';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const visible = list.filter((t) => matchesSection(t, sectionFilter));
    return {
      open: visible.filter((t) => t.completed_at === null),
      completed: visible.filter((t) => t.completed_at !== null),
      openCountBySection: counts,
    };
  }, [todosQ.data, sectionFilter]);

  const totalOpen = (todosQ.data ?? []).filter((t) => t.completed_at === null).length;

  const renderChip = (f: SectionFilter, label: string, count: number) => (
    <button
      key={String(f)}
      type="button"
      className={`chip${sectionFilter === f ? ' is-active' : ''}`}
      onClick={() => selectFilter(f)}
    >
      {label}
      {count > 0 && <span className="chip__count">{count}</span>}
    </button>
  );

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="card-title">
        Todos
        <span style={{ color: 'var(--color-text-soft)', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
          ({open.length} offen{completed.length > 0 ? `, ${completed.length} erledigt` : ''})
        </span>
      </div>

      {/* Sektionsfilter — nur sinnvoll, sobald das Projekt Sektionen hat */}
      {sections.length > 0 && (
        <div className="chip-row" role="group" aria-label="Todos nach Sektion filtern">
          {renderChip('all', 'Alle', totalOpen)}
          {renderChip('none', 'Allgemein', openCountBySection.get('none') ?? 0)}
          {sections.map((s) => renderChip(s.id, s.name, openCountBySection.get(s.id) ?? 0))}
        </div>
      )}

      {/* Anlegen */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        <input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Neues Todo … (Titel)"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleCreate();
            }
          }}
        />
        <textarea
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          placeholder="Beschreibung (optional)"
          rows={2}
          style={{ resize: 'vertical', minHeight: 36 }}
        />
        <div className="todo-add-row">
          {sections.length > 0 && (
            <select
              value={newSectionId ?? ''}
              onChange={(e) => setNewSectionId(e.target.value ? Number(e.target.value) : null)}
              aria-label="Sektion des neuen Todos"
            >
              <option value="">Sektion: Allgemein</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  Sektion: {s.name}
                </option>
              ))}
            </select>
          )}
          {createMut.isError && (
            <div style={{ color: 'var(--color-danger-text)', fontSize: 12 }}>
              {apiErrorMessage(createMut.error)}
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleCreate}
            disabled={!newTitle.trim() || createMut.isPending}
            style={{ marginLeft: 'auto' }}
          >
            {createMut.isPending ? 'Speichere …' : '+ Todo hinzufügen'}
          </button>
        </div>
        {updateMut.isError && (
          <div style={{ color: 'var(--color-danger-text)', fontSize: 12 }}>
            {apiErrorMessage(updateMut.error)}
          </div>
        )}
      </div>

      {/* Liste */}
      {todosQ.isLoading ? (
        <div className="loading">Lade Todos …</div>
      ) : todosQ.error ? (
        <div className="empty-state"><p>Fehler: {apiErrorMessage(todosQ.error)}</p></div>
      ) : (
        <>
          {open.length === 0 && completed.length === 0 && (
            <div className="empty-state" style={{ padding: 20 }}>
              <p>{sectionFilter === 'all' ? 'Keine Todos. Lege oben das erste an.' : 'Keine Todos in dieser Sektion.'}</p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {open.map((t) => (
              <TodoRow
                key={t.id}
                todo={t}
                currentUserId={user?.id ?? null}
                isPrivileged={isPrivileged}
                onToggle={(checked) => toggleMut.mutate({ id: t.id, completed: checked })}
                onUpdate={(vars) => updateMut.mutate(vars)}
                showSection={sectionFilter === 'all' && sections.length > 0}
                onDelete={() => deleteMut.mutate(t.id)}
              />
            ))}
          </div>

          {completed.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                onClick={() => setShowCompleted((s) => !s)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--color-text-soft)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  padding: '6px 0',
                }}
              >
                {showCompleted ? '▾' : '▸'} {completed.length} erledigt
              </button>
              {showCompleted && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                  {completed.map((t) => (
                    <TodoRow
                      key={t.id}
                      todo={t}
                      currentUserId={user?.id ?? null}
                      isPrivileged={isPrivileged}
                      onToggle={(checked) => toggleMut.mutate({ id: t.id, completed: checked })}
                      onUpdate={(vars) => updateMut.mutate(vars)}
                      showSection={sectionFilter === 'all' && sections.length > 0}
                      onDelete={() => deleteMut.mutate(t.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface TodoRowProps {
  todo: TodoWithUsers;
  currentUserId: number | null;
  isPrivileged: boolean;
  onToggle: (completed: boolean) => void;
  onUpdate: (vars: UpdateTodoVars) => void;
  onDelete: () => void;
  /** Sektions-Tag anzeigen (nur im Chip "Alle" sinnvoll). */
  showSection: boolean;
}

function TodoRow({
  todo,
  currentUserId,
  isPrivileged,
  onToggle,
  onUpdate,
  onDelete,
  showSection,
}: TodoRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<TodoDraft>(() => todoToDraft(todo));

  const isOwner = todo.created_by === currentUserId;
  const isDone = todo.completed_at !== null;
  // Edit-Recht: Owner oder admin/PM, und nur solange offen
  const canEdit = (isOwner || isPrivileged) && !isDone;
  // Lösch-Recht: admin/PM immer, Owner nur wenn offen
  const canDelete = isPrivileged || (isOwner && !isDone);

  const startEdit = () => {
    setDraft(todoToDraft(todo));
    setEditing(true);
  };

  const commitEdit = () => {
    if (!draft.title.trim()) return;
    const vars = buildTodoUpdate(todo, draft);
    if (vars) onUpdate(vars);
    setEditing(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: '10px 12px',
        background: isDone ? 'var(--color-surface-alt)' : '#fff',
        border: '1px solid var(--color-border-soft)',
        borderRadius: 8,
        opacity: isDone ? 0.75 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={isDone}
        onChange={(e) => onToggle(e.target.checked)}
        style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0, cursor: 'pointer' }}
        title={isDone ? 'Als offen markieren' : 'Als erledigt markieren'}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <>
            <input
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                if (e.key === 'Escape') setEditing(false);
              }}
              autoFocus
              style={{ width: '100%', fontWeight: 600, fontSize: 14, marginBottom: 6 }}
            />
            <textarea
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              rows={2}
              placeholder="Beschreibung (optional)"
              style={{ width: '100%', resize: 'vertical', minHeight: 32 }}
            />
            <TodoMoveFields
              projectId={draft.projectId}
              sectionId={draft.sectionId}
              onChange={({ projectId, sectionId }) => setDraft((d) => ({ ...d, projectId, sectionId }))}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="button" className="btn btn-primary btn-sm" onClick={commitEdit}>
                Speichern
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>
                Abbrechen
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--color-navy)',
                textDecoration: isDone ? 'line-through' : 'none',
              }}
            >
              {todo.title}
              {showSection && <TodoSectionTag name={todo.section_name} />}
            </div>
            {todo.description && (
              <div style={{ fontSize: 13, color: 'var(--color-text-strong)', marginTop: 3, whiteSpace: 'pre-wrap' }}>
                {todo.description}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--color-text-soft)', marginTop: 6 }}>
              {renderMeta(todo)}
            </div>
          </>
        )}
      </div>

      {!editing && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {canEdit && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={startEdit}
              title="Bearbeiten"
            >
              Bearb.
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onDelete}
              title="Löschen"
              style={{ color: 'var(--color-danger-text)' }}
            >
              ×
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function renderMeta(t: TodoWithUsers): string {
  const created = `${dayjs(t.created_at).format('DD.MM.YYYY HH:mm')} · ${t.created_by_name ?? '—'}`;
  if (t.completed_at) {
    const done = `${dayjs(t.completed_at).format('DD.MM.YYYY HH:mm')} · ${t.completed_by_name ?? '—'}`;
    return `Angelegt: ${created} — Erledigt: ${done}`;
  }
  return `Angelegt: ${created}`;
}
